/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { IPRAttributes, OfferingType } from '.';
import { AttributeSection } from './attribute-guidance';

/** A single attribute value shown as a chip, tagged with whether it is inherited-only. */
export interface AttributeChipModel {
  label: string;
  /**
   * True when this value is present only because it cascades from an ancestor (PLT-5695) — the node
   * itself doesn't set (scalar) or contribute (array) it. Rendered with the secondary/italic
   * "(inherited)" treatment mirroring the taxonomy-mapping cell.
   */
  inherited: boolean;
  /**
   * True when a person set this attribute by hand rather than the auto-matching. Rendered with the
   * orange treatment the product selector uses for a manual match.
   */
  manual?: boolean;
}

export interface AttributeGroupModel {
  /** The Edit product dialog section these values are set in, whose guidance explains them. */
  section: AttributeSection;
  title: string;
  chips: AttributeChipModel[];
}

/**
 * Turn a resolved IPR blob (own merged with whatever cascades from ancestors) plus the node's OWN
 * merged IPR into the grouped, labelled chips shown in the Attributes column and the "Inherited
 * from parent" hint in the edit dialog. Sharing one builder keeps the labels and the inherited-only
 * classification identical between the list and the modal (PLT-5799), and between the editor and
 * what the chat reads of a custom company's product set (PLT-7304), so the chat names an attribute
 * the way the user sees it.
 *
 * A value is inherited-only when the node itself doesn't contribute it: for an array field, the
 * value is not in the node's own set; for a scalar, the node's own value is `undefined` yet the
 * resolved blob still carries one.
 */
export function buildAttributeGroups(
  resolvedIpr: Partial<IPRAttributes>,
  ownIpr: Partial<IPRAttributes>,
  /** Keys a person set by hand (e.g. `product_mixin.ipraManualFields`); their chips get `manual`. */
  manualFields: readonly string[] = [],
): AttributeGroupModel[] {
  const isManual = (field: keyof IPRAttributes): boolean => manualFields.includes(field);
  const arrayChips = (field: keyof IPRAttributes): AttributeChipModel[] => {
    const resolvedArr = (resolvedIpr[field] as unknown[] | undefined) ?? [];
    const ownArr = (ownIpr[field] as unknown[] | undefined) ?? [];
    return resolvedArr.map(value => ({
      label: String(value),
      inherited: !ownArr.includes(value),
      manual: isManual(field),
    }));
  };
  const scalarFlags = (field: keyof IPRAttributes): Pick<AttributeChipModel, 'inherited' | 'manual'> => ({
    inherited: ownIpr[field] === undefined,
    manual: isManual(field),
  });

  const industries = arrayChips('TargetIndustry');
  const certificates = arrayChips('Certifications');
  const recyclability = arrayChips('Recyclability');
  const energySources = arrayChips('EnergySourcesUsed');
  const absentRawMaterials = arrayChips('MaterialsAbsences');

  const specialEndUses: AttributeChipModel[] = [];
  if (resolvedIpr.Educational) specialEndUses.push({ label: 'Educational', ...scalarFlags('Educational') });
  if (resolvedIpr.ForHumanitarianAid)
    specialEndUses.push({ label: 'For Humanitarian Aid', ...scalarFlags('ForHumanitarianAid') });
  if (resolvedIpr.ForDisabledPeople)
    specialEndUses.push({ label: 'For Disabled People', ...scalarFlags('ForDisabledPeople') });
  if (resolvedIpr.ForLanguageMinorityGroups)
    specialEndUses.push({
      label: 'For Language Minority Groups',
      ...scalarFlags('ForLanguageMinorityGroups'),
    });

  const otherSpecialAttributes: AttributeChipModel[] = [];
  if (resolvedIpr.FollowingJournalisticEthics)
    otherSpecialAttributes.push({
      label: 'Following Journalistic Ethics',
      ...scalarFlags('FollowingJournalisticEthics'),
    });
  if (resolvedIpr.InDevelopingCountries)
    otherSpecialAttributes.push({
      label: 'In developing countries',
      ...scalarFlags('InDevelopingCountries'),
    });
  if (resolvedIpr.WithoutAnimalTesting)
    otherSpecialAttributes.push({
      label: 'Without Animal Testing',
      ...scalarFlags('WithoutAnimalTesting'),
    });
  if (resolvedIpr.UltraLowCostCommerce)
    otherSpecialAttributes.push({
      label: 'Ultra Low Cost Commerce',
      ...scalarFlags('UltraLowCostCommerce'),
    });

  const productInformation: AttributeChipModel[] = [];
  if (resolvedIpr.OfferingType === OfferingType.Product)
    productInformation.push({ label: 'Offering type: Product', ...scalarFlags('OfferingType') });
  if (resolvedIpr.OfferingType === OfferingType.Service)
    productInformation.push({ label: 'Offering type: Service', ...scalarFlags('OfferingType') });
  if (resolvedIpr.isManufactured !== undefined)
    productInformation.push({
      label: resolvedIpr.isManufactured ? 'Manufactured by the company' : 'Not manufactured by the company',
      ...scalarFlags('isManufactured'),
    });
  if (resolvedIpr.Tangibility)
    productInformation.push({
      label: `Tangibility: ${resolvedIpr.Tangibility}`,
      ...scalarFlags('Tangibility'),
    });

  const businessModelAttributes: AttributeChipModel[] = [];
  if (resolvedIpr.IsPhysicalRetail)
    businessModelAttributes.push({ label: 'Physical Retail', ...scalarFlags('IsPhysicalRetail') });
  if (resolvedIpr.IsOnlineRetail)
    businessModelAttributes.push({ label: 'Online Retail', ...scalarFlags('IsOnlineRetail') });
  if (resolvedIpr.IsWholesale) businessModelAttributes.push({ label: 'Wholesale', ...scalarFlags('IsWholesale') });
  if (resolvedIpr.IsRental) businessModelAttributes.push({ label: 'Rental', ...scalarFlags('IsRental') });
  if (resolvedIpr.IsLeasing) businessModelAttributes.push({ label: 'Leasing', ...scalarFlags('IsLeasing') });
  if (resolvedIpr.IsTrading) businessModelAttributes.push({ label: 'Trading', ...scalarFlags('IsTrading') });
  if (resolvedIpr.IsInstallationOf)
    businessModelAttributes.push({ label: 'Installation of', ...scalarFlags('IsInstallationOf') });
  if (resolvedIpr.IsAssemblyOf) businessModelAttributes.push({ label: 'Assembly of', ...scalarFlags('IsAssemblyOf') });
  if (resolvedIpr.IsRepairOrMaintenanceOf)
    businessModelAttributes.push({
      label: 'Repair or maintenance of',
      ...scalarFlags('IsRepairOrMaintenanceOf'),
    });
  if (resolvedIpr.IsDistributionOf)
    businessModelAttributes.push({ label: 'Distribution of', ...scalarFlags('IsDistributionOf') });
  if (resolvedIpr.IsLicensingOf)
    businessModelAttributes.push({ label: 'Licensing of', ...scalarFlags('IsLicensingOf') });

  return [
    { section: 'targetIndustry', title: 'For industries', chips: industries },
    { section: 'certificates', title: 'Certificates', chips: certificates },
    { section: 'recyclability', title: 'Recyclability', chips: recyclability },
    { section: 'energySources', title: 'Using energy', chips: energySources },
    { section: 'specialEndUses', title: 'Special end uses', chips: specialEndUses },
    { section: 'absentRawMaterials', title: 'Absent raw materials', chips: absentRawMaterials },
    { section: 'businessModel', title: 'Business model', chips: businessModelAttributes },
    // Ultra low cost commerce is listed here, though the edit dialog sets it under Business model.
    { section: 'specialAttributes', title: 'Special attributes', chips: otherSpecialAttributes },
    { section: 'productType', title: 'Product information', chips: productInformation },
  ];
}
